import { Plus, Trash2 } from 'lucide-react';

import {
    formFieldGroupNestedCardClassName,
    formFieldGroupSurfaceClassName,
} from '../../ui/formPrimitives';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { cn } from '../../../lib/utils';
import {
    createEmptyPricingSampleSizeRow,
    type PricingConfigFormValues,
    type PricingSampleSizeFormRow,
} from './pricingConfigForm';
import { Field } from './creditDialogPrimitives';

const MAX_PRICING_SAMPLE_SIZES = 10;

export function PricingConfigFields({
    values,
    onChange,
}: {
    values: PricingConfigFormValues;
    onChange: (values: PricingConfigFormValues) => void;
}) {
    const update = <K extends keyof PricingConfigFormValues>(
        key: K,
        value: PricingConfigFormValues[K],
    ) => {
        onChange({ ...values, [key]: value });
    };

    const updateSampleSize = (
        key: string,
        field: keyof Pick<PricingSampleSizeFormRow, 'n' | 'multiplier'>,
        value: string,
    ) => {
        update(
            'sampleSizes',
            values.sampleSizes.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
        );
    };

    const addSampleSize = () => {
        if (values.sampleSizes.length >= MAX_PRICING_SAMPLE_SIZES) return;
        update('sampleSizes', [...values.sampleSizes, createEmptyPricingSampleSizeRow()]);
    };

    const removeSampleSize = (key: string) => {
        if (values.sampleSizes.length <= 1) return;
        const nextRows = values.sampleSizes.filter((row) => row.key !== key);
        const defaultStillExists = nextRows.some((row) => row.n === values.defaultSampleSize);
        onChange({
            ...values,
            sampleSizes: nextRows,
            defaultSampleSize: defaultStillExists
                ? values.defaultSampleSize
                : (nextRows[0]?.n ?? ''),
        });
    };

    return (
        <>
            <div className="grid gap-4 md:grid-cols-2">
                <Field label="Name" required>
                    <Input
                        value={values.name}
                        onChange={(event) => update('name', event.target.value)}
                    />
                </Field>
                <Field label="Description">
                    <Input
                        value={values.description}
                        onChange={(event) => update('description', event.target.value)}
                    />
                </Field>
            </div>
            <Field label="Unit type" required>
                <Select
                    ariaLabel="Pricing unit type"
                    value={values.unitType}
                    options={[
                        { value: 'credit', label: 'Credit' },
                        { value: 'token', label: 'Token' },
                    ]}
                    onValueChange={(value) => {
                        if (value === 'credit' || value === 'token') {
                            update('unitType', value);
                        }
                    }}
                />
            </Field>

            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                <div>
                    <h3 className="text-sm font-semibold text-neutral-950">Pro</h3>
                    <p className="text-xs text-neutral-600">
                        Pricing inputs for Pro estimates, B2B/B2C costs, and sample-size
                        multipliers.
                    </p>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-neutral-800">
                    <input
                        type="checkbox"
                        checked={values.supportsPricingEstimates}
                        onChange={(event) =>
                            update('supportsPricingEstimates', event.target.checked)
                        }
                        className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                    />
                    Supports pricing estimates
                </label>
                <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Sentiment map multiplier" required>
                        <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            value={values.sentimentMapMultiplier}
                            onChange={(event) =>
                                update('sentimentMapMultiplier', event.target.value)
                            }
                        />
                    </Field>
                    <Field label="B2B credits" required>
                        <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            value={values.b2bCredits}
                            onChange={(event) => update('b2bCredits', event.target.value)}
                        />
                    </Field>
                    <Field label="B2C credits" required>
                        <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            value={values.b2cCredits}
                            onChange={(event) => update('b2cCredits', event.target.value)}
                        />
                    </Field>
                </div>
                <div className={cn(formFieldGroupNestedCardClassName, 'space-y-3')}>
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <h4 className="text-sm font-semibold text-neutral-950">Sample Sizes</h4>
                            <p className="text-xs text-neutral-600">
                                Internal list for this Pro pricing section. Add up to{' '}
                                {MAX_PRICING_SAMPLE_SIZES} sample-size multipliers.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={addSampleSize}
                            disabled={values.sampleSizes.length >= MAX_PRICING_SAMPLE_SIZES}
                        >
                            <Plus className="mr-2 size-4" aria-hidden />
                            Add Sample Size
                        </Button>
                    </div>
                    {values.sampleSizes.map((row, index) => (
                        <div
                            key={row.key}
                            className={cn(
                                formFieldGroupNestedCardClassName,
                                'grid gap-3 md:grid-cols-[1fr_1fr_auto]',
                            )}
                        >
                            <Field label={`Sample size ${index + 1}`} required>
                                <Input
                                    variant="outlined"
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    step={1}
                                    value={row.n}
                                    onChange={(event) =>
                                        updateSampleSize(row.key, 'n', event.target.value)
                                    }
                                />
                            </Field>
                            <Field label="Multiplier" required>
                                <Input
                                    variant="outlined"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step="any"
                                    value={row.multiplier}
                                    onChange={(event) =>
                                        updateSampleSize(row.key, 'multiplier', event.target.value)
                                    }
                                />
                            </Field>
                            <div className="flex items-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="border-red-200 text-red-700 hover:bg-red-50"
                                    onClick={() => removeSampleSize(row.key)}
                                    disabled={values.sampleSizes.length <= 1}
                                    aria-label={`Remove sample size ${index + 1}`}
                                >
                                    <Trash2 className="size-4" aria-hidden />
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Field label="Default sample size" required>
                        <Select
                            ariaLabel="Default sample size"
                            value={values.defaultSampleSize}
                            placeholder="Select default sample size"
                            options={values.sampleSizes
                                .filter((row) => row.n.trim() !== '')
                                .map((row) => ({ value: row.n, label: row.n }))}
                            onValueChange={(value) => update('defaultSampleSize', value)}
                        />
                    </Field>
                </div>
            </section>

            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                <div>
                    <h3 className="text-sm font-semibold text-neutral-950">Pulse</h3>
                    <p className="text-xs text-neutral-600">
                        Pricing inputs for standard Pulse usage.
                    </p>
                </div>
                <Field label="Standard Pulse" required>
                    <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="any"
                        value={values.standardPulse}
                        onChange={(event) => update('standardPulse', event.target.value)}
                    />
                </Field>
            </section>
        </>
    );
}
